import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useGrowthQuery } from '../api/growth.queries';
import {
  useWatchlistsQuery,
  useCreateWatchlistMutation,
  useUpdateWatchlistMutation,
  useDeleteWatchlistMutation,
} from '../api/watchlists.queries';
import { usePriceSparkQuery } from '../../prices/api/spark.queries';
import { useLivePricesQuery } from '../../prices/api/live.queries';
import SymbolWithLivePrice from '../../../shared/components/SymbolWithLivePrice';
import { httpJSON } from '../../../shared/api/http';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import {
  PERIODS,
  FUNDAMENTAL_METRICS,
  FUND_FILTER_MODES,
  WATCHLIST_STATE_VERSION,
  sanitizeGrowthState,
  normalizeGrowthFilters,
  GROWTH_COMPARATORS,
} from '../lib/watchlistState';

const STORAGE_KEY = 'growthTableState_v2';
const SPARK_WINDOW_DAYS = 365;
const DEFAULT_WATCHLIST_NAME = 'New watchlist';

const loadStoredState = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const filterSeriesToWindow = (series, windowDays = SPARK_WINDOW_DAYS) => {
  if (!Array.isArray(series) || series.length === 0) return [];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - windowDays);

  const trimmed = series.filter(([dateString]) => {
    const parsed = new Date(dateString);
    return !Number.isNaN(parsed.getTime()) && parsed >= cutoff;
  });

  return trimmed.length ? trimmed : series.slice(-windowDays);
};

function SparklinePreviewCard({ symbol, cache, setCache, livePrice }) {
  const { data, isLoading, isError } = usePriceSparkQuery(symbol, {
    enabled: Boolean(symbol),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!symbol) return;
    const series = filterSeriesToWindow(data?.series);
    if (!Array.isArray(series) || series.length === 0) return;
    setCache((prev) => {
      const prevSeries = prev[symbol];
      if (Array.isArray(prevSeries) && prevSeries.length === series.length) {
        const lastPrev = prevSeries[prevSeries.length - 1];
        const lastCurr = series[series.length - 1];
        if (lastPrev && lastCurr && lastPrev[0] === lastCurr[0] && lastPrev[1] === lastCurr[1]) {
          return prev;
        }
      }
      return { ...prev, [symbol]: series };
    });
  }, [symbol, data, setCache]);

  if (!symbol) {
    return (
      <div className="spark-preview-wrapper">
        <div className="sparkline-placeholder subtle">Hover a symbol to preview price history.</div>
      </div>
    );
  }

  const series = filterSeriesToWindow(cache[symbol] || data?.series || []);
  const hasSeries = series.length >= 2;
  const showSkeleton = !hasSeries && isLoading;
  const showError = !hasSeries && isError;
  const showNoData = !hasSeries && !isLoading && !isError;
  const chartData = series.map(([d, v]) => ({ date: d, value: Number(v) }));

  return (
    <div className="spark-preview-wrapper">
      <div className="sparkline-preview-card">
        <h4>
          <SymbolWithLivePrice symbol={symbol} price={livePrice} />
        </h4>
        {showSkeleton ? (
          <div className="sparkline-skeleton" style={{ height: '160px' }} aria-hidden="true" />
        ) : showError ? (
          <p className="subtle">Unable to load prices.</p>
        ) : showNoData ? (
          <p className="subtle">Not enough data for sparkline.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={chartData} margin={{ left: 0, right: 0, top: 10, bottom: 10 }}>
                <CartesianGrid vertical={false} stroke="#1f2937" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => value.slice(5)}
                  minTickGap={20}
                  stroke="#64748b"
                />
                <YAxis
                  dataKey="value"
                  stroke="#64748b"
                  width={50}
                  tickFormatter={(val) => val.toFixed(2)}
                />
                <ChartTooltip
                  formatter={(val) => [Number(val).toFixed(2), 'Price']}
                  labelFormatter={(label) => label}
                  contentStyle={{ backgroundColor: '#0d1423', borderColor: '#1f2937' }}
                />
                <Line type="monotone" dataKey="value" stroke="#38bdf8" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
    </div>
  );
}

const SparklinePreview = React.memo(SparklinePreviewCard);

function MultiSelectFilter({
  label,
  options,
  selected,
  search,
  onSearchChange,
  onToggle,
  onSetAll,
  onSetNone,
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [panelStyle, setPanelStyle] = React.useState(null);
  const panelRef = React.useRef(null);
  const triggerRef = React.useRef(null);

  const closePanel = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const updatePanelPosition = React.useCallback(() => {
    if (typeof window === 'undefined' || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const MIN_WIDTH = 320;
    const MAX_WIDTH = 480;
    const ideal = Math.max(rect.width * 2, MIN_WIDTH);
    const width = Math.min(Math.max(ideal, MIN_WIDTH), Math.min(MAX_WIDTH, viewportWidth - 32));
    const left = Math.min(Math.max(rect.left + rect.width / 2 - width / 2, 16), viewportWidth - width - 16);

    const safeHeight = Math.max(280, Math.min(520, viewportHeight - 48));
    const desiredTop = rect.bottom + 12;
    const maxTop = viewportHeight ? viewportHeight - safeHeight - 16 : desiredTop;
    const clampedTop = Math.max(16, Math.min(desiredTop, Math.max(16, maxTop)));

    setPanelStyle({
      left,
      top: clampedTop,
      width,
    });
  }, []);

  const handleTriggerClick = () => {
    setIsOpen((prev) => {
      if (prev) {
        return false;
      }
      updatePanelPosition();
      return true;
    });
  };

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handleResize = () => updatePanelPosition();
    const handleScroll = () => updatePanelPosition();
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        closePanel();
      }
    };
    const handleClick = (event) => {
      if (panelRef.current && panelRef.current.contains(event.target)) return;
      if (triggerRef.current && triggerRef.current.contains(event.target)) return;
      closePanel();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [closePanel, isOpen, updatePanelPosition]);

  const effectiveSelected = selected === null ? null : selected;

  const filteredOptions = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => opt.toLowerCase().includes(term));
  }, [options, search]);

  const checkedCount = effectiveSelected === null ? options.length : effectiveSelected.length;
  const summaryLabel = options.length && checkedCount !== options.length
    ? `${label} (${checkedCount}/${options.length})`
    : label;

  const isChecked = (option) => effectiveSelected === null || effectiveSelected.includes(option);

  return (
    <div className="multi-filter">
      <button
        type="button"
        ref={triggerRef}
        className="ghost sm multi-filter-trigger"
        onClick={handleTriggerClick}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {summaryLabel}
      </button>
      {isOpen && panelStyle && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
          <div className="multi-filter-overlay" role="presentation">
            <div className="multi-filter-backdrop" onClick={closePanel} />
            <div
              className="multi-filter-panel"
              ref={panelRef}
              role="dialog"
              aria-label={`${label} filter options`}
              style={{
                left: `${panelStyle.left}px`,
                top: `${panelStyle.top}px`,
                width: `${panelStyle.width}px`,
              }}
            >
              <header className="multi-filter-header">
                <span>{label}</span>
                <div className="multi-filter-actions">
                  <button type="button" className="ghost xs" onClick={onSetAll}>
                    All
                  </button>
                  <button type="button" className="ghost xs" onClick={onSetNone} disabled={options.length === 0}>
                    None
                  </button>
                </div>
              </header>
              <input
                className="multi-filter-search"
                type="search"
                value={search}
                placeholder="Search..."
                onChange={(evt) => onSearchChange(evt.target.value)}
              />
              <div className="multi-filter-options">
                {filteredOptions.length === 0 ? (
                  <p className="subtle">No matches.</p>
                ) : (
                  filteredOptions.map((option) => (
                    <label key={option} className="multi-filter-option">
                      <input
                        type="checkbox"
                        checked={isChecked(option)}
                        onChange={() => onToggle(option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

export default function GrowthTable() {
  const storedState = useMemo(loadStoredState, []);
  const initialState = useMemo(() => sanitizeGrowthState(storedState), [storedState]);
  const { data, isLoading, isError, error, refetch, isFetching } = useGrowthQuery();

  const [filters, setFilters] = useState(() => normalizeGrowthFilters(initialState.filters));
  const [fundFilters, setFundFilters] = useState(initialState.fundFilters);
  const [stopLoss, setStopLoss] = useState(initialState.stopLoss);
  const [sortState, setSortState] = useState(initialState.sortState);
  const [hoveredSymbol, setHoveredSymbol] = useState(null);
  const [expandedSymbol, setExpandedSymbol] = useState(null);
  const [pinnedSymbols, setPinnedSymbols] = useState(initialState.pinnedSymbols);
  const [sparkCache, setSparkCache] = useState({});
  const [probeSymbol, setProbeSymbol] = useState('AAPL');
  const [probeLoading, setProbeLoading] = useState(false);
  const [probeResult, setProbeResult] = useState(null);
  const [probeError, setProbeError] = useState(null);
  const [probeCheckedAt, setProbeCheckedAt] = useState(null);
  const [countryFilter, setCountryFilter] = useState(initialState.metaFilters.country);
  const [symbolTypeFilter, setSymbolTypeFilter] = useState(initialState.metaFilters.symbol_type);
  const [industryFilter, setIndustryFilter] = useState(initialState.metaFilters.industries);
  const [countrySearch, setCountrySearch] = useState('');
  const [symbolTypeSearch, setSymbolTypeSearch] = useState('');
  const [industrySearch, setIndustrySearch] = useState('');
  const [filtersCollapsed, setFiltersCollapsed] = useState(initialState.filtersCollapsed);
  const [isWatchlistOpen, setIsWatchlistOpen] = useState(false);
  const [watchlistError, setWatchlistError] = useState(null);
  const [watchlistMessage, setWatchlistMessage] = useState(null);
  const [activeWatchlist, setActiveWatchlist] = useState(null);
  const [pendingOverwriteId, setPendingOverwriteId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [nameDraft, setNameDraft] = useState(DEFAULT_WATCHLIST_NAME);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [nameModalValue, setNameModalValue] = useState('');
  const [nameModalError, setNameModalError] = useState(null);

  const {
    data: watchlistsData,
    isLoading: watchlistsLoading,
    isError: watchlistsIsError,
    error: watchlistsErrorObj,
    refetch: refetchWatchlists,
  } = useWatchlistsQuery();
  const watchlists = useMemo(
    () => (Array.isArray(watchlistsData) ? watchlistsData : []),
    [watchlistsData],
  );

  const {
    mutateAsync: createWatchlistMutateAsync,
    isPending: isCreateWatchlistPending,
  } = useCreateWatchlistMutation();
  const {
    mutateAsync: updateWatchlistMutateAsync,
    isPending: isUpdateWatchlistPending,
  } = useUpdateWatchlistMutation();
  const { mutateAsync: deleteWatchlistMutateAsync } = useDeleteWatchlistMutation();
  const creatingWatchlist = isCreateWatchlistPending;
  const updatingWatchlist = pendingOverwriteId !== null || isUpdateWatchlistPending;

  const storageSnapshot = useMemo(
    () => ({
      filters,
      fundFilters,
      sortState,
      pinnedSymbols,
      metaFilters: {
        country: countryFilter,
        symbol_type: symbolTypeFilter,
        industries: industryFilter,
      },
      filtersCollapsed,
      stopLoss,
    }),
    [
      filters,
      fundFilters,
      sortState,
      pinnedSymbols,
      countryFilter,
      symbolTypeFilter,
      industryFilter,
      filtersCollapsed,
      stopLoss,
    ],
  );

  const currentStatePayload = useMemo(
    () => ({
      version: WATCHLIST_STATE_VERSION,
      ...storageSnapshot,
    }),
    [storageSnapshot],
  );

  const initialPayloadStringRef = useRef(JSON.stringify({ version: WATCHLIST_STATE_VERSION, ...initialState }));
  const lastSavedPayloadRef = useRef(initialPayloadStringRef.current);
  const autoSaveTimerRef = useRef(null);
  const toastTimerRef = useRef(null);
  const nameInputRef = useRef(null);
  const nameModalInputRef = useRef(null);

  const syncUrl = useCallback((id) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (id != null) {
      url.searchParams.set('watchlist', String(id));
    } else {
      url.searchParams.delete('watchlist');
    }
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const [pendingWatchlistId, setPendingWatchlistId] = useState(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get('watchlist');
    const parsed = idParam ? Number(idParam) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });

  const applyWatchlistState = useCallback(
    (rawState) => {
      const next = sanitizeGrowthState(rawState);
      setFilters(normalizeGrowthFilters(next.filters));
      setFundFilters(next.fundFilters);
      setSortState(next.sortState);
      setPinnedSymbols(next.pinnedSymbols);
      setCountryFilter(next.metaFilters.country);
      setSymbolTypeFilter(next.metaFilters.symbol_type);
      setIndustryFilter(next.metaFilters.industries);
      setFiltersCollapsed(next.filtersCollapsed);
      setStopLoss(next.stopLoss);
      setHoveredSymbol(null);
      setExpandedSymbol(null);
    },
    [
      setFilters,
      setFundFilters,
      setSortState,
      setPinnedSymbols,
      setCountryFilter,
      setSymbolTypeFilter,
      setIndustryFilter,
      setFiltersCollapsed,
      setStopLoss,
    ],
  );

  const handleOpenLoadModal = useCallback(() => {
    setIsWatchlistOpen(true);
  }, []);

  const handleCreateWatchlist = useCallback(
    async (name) => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      setWatchlistError(null);
      setWatchlistMessage(null);
      try {
        const result = await createWatchlistMutateAsync({
          name: trimmed,
          payload: currentStatePayload,
        });
        const sanitized = sanitizeGrowthState(currentStatePayload);
        setActiveWatchlist({ id: result.id, name: result.name });
        lastSavedPayloadRef.current = JSON.stringify({
          version: WATCHLIST_STATE_VERSION,
          ...sanitized,
        });
        setWatchlistMessage(`Saved "${result.name}"`);
        setNameDraft(result.name);
        setIsEditingName(false);
        setAutoSaveEnabled(false);
        syncUrl(result.id);
        refetchWatchlists();
        return result;
      } catch (err) {
        setWatchlistError(err instanceof Error ? err.message : 'Unable to save watchlist');
        return null;
      }
    },
    [createWatchlistMutateAsync, currentStatePayload, refetchWatchlists, syncUrl],
  );

  const handleOverwriteWatchlist = useCallback(
    async (watchlist) => {
      if (!watchlist) return;
      setPendingOverwriteId(watchlist.id);
      setWatchlistError(null);
      setWatchlistMessage(null);
      try {
        const result = await updateWatchlistMutateAsync({
          id: watchlist.id,
          name: watchlist.name,
          payload: currentStatePayload,
        });
        setActiveWatchlist({ id: result.id, name: result.name });
        const sanitized = sanitizeGrowthState(currentStatePayload);
        lastSavedPayloadRef.current = JSON.stringify({
          version: WATCHLIST_STATE_VERSION,
          ...sanitized,
        });
        setWatchlistMessage(`Updated "${result.name}"`);
        syncUrl(result.id);
        refetchWatchlists();
      } catch (err) {
        setWatchlistError(err instanceof Error ? err.message : 'Unable to update watchlist');
      } finally {
        setPendingOverwriteId(null);
      }
    },
    [currentStatePayload, refetchWatchlists, syncUrl, updateWatchlistMutateAsync],
  );

  const handleLoadWatchlist = useCallback(
    (watchlist) => {
      if (!watchlist) return;
      const sanitized = sanitizeGrowthState(watchlist.payload);
      applyWatchlistState(sanitized);
      setActiveWatchlist({ id: watchlist.id, name: watchlist.name });
      setNameDraft(watchlist.name);
      lastSavedPayloadRef.current = JSON.stringify({
        version: WATCHLIST_STATE_VERSION,
        ...sanitized,
      });
      setWatchlistError(null);
      setWatchlistMessage(null);
      setIsEditingName(false);
      syncUrl(watchlist.id);
      setIsWatchlistOpen(false);
    },
    [applyWatchlistState, syncUrl],
  );

  const handleDeleteWatchlist = useCallback(
    async (watchlist) => {
      if (!watchlist) return;
      const confirmed = typeof window !== 'undefined'
        ? window.confirm(`Delete watchlist "${watchlist.name}"?`)
        : true;
      if (!confirmed) return;
      setPendingDeleteId(watchlist.id);
      setWatchlistError(null);
      setWatchlistMessage(null);
      try {
        await deleteWatchlistMutateAsync(watchlist.id);
        if (activeWatchlist?.id === watchlist.id) {
          setActiveWatchlist(null);
          lastSavedPayloadRef.current = initialPayloadStringRef.current;
          setAutoSaveEnabled(false);
          setNameDraft(DEFAULT_WATCHLIST_NAME);
          setIsEditingName(false);
          syncUrl(null);
        }
        setPendingWatchlistId((prev) => (prev === watchlist.id ? null : prev));
        setWatchlistMessage(`Deleted "${watchlist.name}"`);
        refetchWatchlists();
      } catch (err) {
        setWatchlistError(err instanceof Error ? err.message : 'Unable to delete watchlist');
      } finally {
        setPendingDeleteId(null);
      }
    },
    [activeWatchlist, deleteWatchlistMutateAsync, initialPayloadStringRef, refetchWatchlists, syncUrl],
  );

  const hasActiveWatchlist = Boolean(activeWatchlist);
  const currentPayloadString = JSON.stringify(currentStatePayload);
  const hasChangesFromActive = useMemo(
    () => currentPayloadString !== lastSavedPayloadRef.current,
    [currentPayloadString],
  );

  const handleSaveWatchlist = useCallback(() => {
    const trimmed = nameDraft.trim();
    if (!hasActiveWatchlist) {
      if (!trimmed || trimmed === DEFAULT_WATCHLIST_NAME) {
        setNameModalValue(trimmed === DEFAULT_WATCHLIST_NAME ? '' : trimmed);
        setNameModalError(null);
        setIsNameModalOpen(true);
        return;
      }
      const exists = watchlists.some((item) => item.name.toLowerCase() === trimmed.toLowerCase());
      if (exists) {
        setWatchlistError('Watchlist name already exists');
        return;
      }
      if (!creatingWatchlist) {
        handleCreateWatchlist(trimmed);
      }
      return;
    }
    if (!updatingWatchlist) {
      handleOverwriteWatchlist(activeWatchlist);
    }
  }, [activeWatchlist, creatingWatchlist, handleCreateWatchlist, handleOverwriteWatchlist, hasActiveWatchlist, nameDraft, updatingWatchlist, watchlists]);

  useEffect(() => {
    if (hasActiveWatchlist) {
      setNameDraft(activeWatchlist?.name ?? DEFAULT_WATCHLIST_NAME);
    } else {
      setNameDraft((prev) => (prev && prev !== DEFAULT_WATCHLIST_NAME ? prev : DEFAULT_WATCHLIST_NAME));
    }
    setIsEditingName(false);
  }, [hasActiveWatchlist, activeWatchlist]);

  useEffect(() => {
    if (!hasActiveWatchlist) {
      setAutoSaveEnabled(false);
    }
  }, [hasActiveWatchlist]);

  const cancelNameEdit = useCallback(() => {
    if (hasActiveWatchlist) {
      setNameDraft(activeWatchlist?.name ?? DEFAULT_WATCHLIST_NAME);
    } else {
      setNameDraft(DEFAULT_WATCHLIST_NAME);
    }
    setIsEditingName(false);
  }, [activeWatchlist, hasActiveWatchlist]);

  const commitNameEdit = useCallback(async () => {
    const trimmed = nameDraft.trim();
    if (!hasActiveWatchlist) {
      setNameDraft(trimmed || DEFAULT_WATCHLIST_NAME);
      setIsEditingName(false);
      return;
    }
    if (!trimmed) {
      setNameDraft(activeWatchlist.name);
      setIsEditingName(false);
      return;
    }
    if (trimmed === activeWatchlist.name) {
      setIsEditingName(false);
      return;
    }
    try {
      const result = await updateWatchlistMutateAsync({
        id: activeWatchlist.id,
        name: trimmed,
      });
      setActiveWatchlist({ id: result.id, name: result.name });
      setNameDraft(result.name);
      setWatchlistMessage(`Renamed to "${result.name}"`);
      setIsEditingName(false);
      refetchWatchlists();
    } catch (err) {
      setWatchlistError(err instanceof Error ? err.message : 'Unable to rename watchlist');
      setNameDraft(activeWatchlist.name);
      setIsEditingName(false);
    }
  }, [activeWatchlist, hasActiveWatchlist, nameDraft, refetchWatchlists, updateWatchlistMutateAsync]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  useEffect(() => {
    if (isNameModalOpen && nameModalInputRef.current) {
      nameModalInputRef.current.focus();
      nameModalInputRef.current.select();
    }
  }, [isNameModalOpen]);

  useEffect(() => {
    if (pendingWatchlistId == null) return;
    if (!Array.isArray(watchlists) || watchlists.length === 0) return;
    const target = watchlists.find((item) => item.id === pendingWatchlistId);
    if (target) {
      handleLoadWatchlist(target);
      setPendingWatchlistId(null);
    } else if (!watchlistsLoading) {
      setPendingWatchlistId(null);
      syncUrl(null);
    }
  }, [handleLoadWatchlist, pendingWatchlistId, syncUrl, watchlists, watchlistsLoading]);

  useEffect(() => {
    if (!watchlistMessage) {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
      return undefined;
    }
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setWatchlistMessage(null);
      toastTimerRef.current = null;
    }, 3000);
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, [watchlistMessage]);

  const handleNameModalConfirm = useCallback(async () => {
    const trimmed = nameModalValue.trim();
    if (!trimmed) {
      setNameModalError('Name is required');
      return;
    }
    const exists = watchlists.some((item) => item.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setNameModalError('Watchlist name already exists');
      return;
    }
    const result = await handleCreateWatchlist(trimmed);
    if (result) {
      setIsNameModalOpen(false);
      setNameModalValue('');
      setNameModalError(null);
    }
  }, [handleCreateWatchlist, nameModalValue, watchlists]);

  const handleNameModalCancel = useCallback(() => {
    setIsNameModalOpen(false);
    setNameModalValue('');
    setNameModalError(null);
  }, []);

  useEffect(() => {
    if (!isNameModalOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        handleNameModalCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleNameModalCancel, isNameModalOpen]);

  const handleAutoSaveToggle = useCallback(
    (event) => {
      const next = event.target.checked;
      if (next && !hasActiveWatchlist) {
        setWatchlistMessage('Load a watchlist to enable auto save.');
        setAutoSaveEnabled(false);
        return;
      }
      setAutoSaveEnabled(next);
    },
    [hasActiveWatchlist],
  );

  useEffect(() => {
    if (!isWatchlistOpen) {
      setPendingOverwriteId(null);
      setPendingDeleteId(null);
    }
  }, [isWatchlistOpen]);

  useEffect(() => {
    if (!isWatchlistOpen) return undefined;
    const handler = (event) => {
      if (event.key === 'Escape') {
        setIsWatchlistOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isWatchlistOpen]);

  useEffect(() => () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!autoSaveEnabled || !activeWatchlist) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return undefined;
    }

    const serialized = JSON.stringify(currentStatePayload);
    if (serialized === lastSavedPayloadRef.current) {
      return undefined;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      updateWatchlistMutateAsync({
        id: activeWatchlist.id,
        name: activeWatchlist.name,
        payload: currentStatePayload,
      })
        .then(() => {
          const sanitized = sanitizeGrowthState(currentStatePayload);
          lastSavedPayloadRef.current = JSON.stringify({
            version: WATCHLIST_STATE_VERSION,
            ...sanitized,
          });
          setWatchlistMessage(`Auto-saved "${activeWatchlist.name}"`);
        })
        .catch((err) => {
          setWatchlistError(err instanceof Error ? err.message : 'Unable to auto-save watchlist');
        })
        .finally(() => {
          autoSaveTimerRef.current = null;
        });
    }, 750);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [autoSaveEnabled, activeWatchlist, currentStatePayload, updateWatchlistMutateAsync]);

  const formatWatchlistTime = useCallback((isoString) => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  }, []);

  const watchlistsFetchError = watchlistsIsError
    ? (watchlistsErrorObj instanceof Error
      ? watchlistsErrorObj.message
      : String(watchlistsErrorObj ?? 'Unable to load watchlists'))
    : null;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(storageSnapshot));
    } catch {
      /* ignore storage errors */
    }
  }, [storageSnapshot]);

  const passesGrowthFilter = useCallback((rowValue, config) => {
    if (!config) return true;
    const rawValue = config.value;
    if (rawValue === undefined || rawValue === null || rawValue === '') return true;
    const threshold = parseFloat(rawValue);
    if (Number.isNaN(threshold)) return true;
    if (rowValue === null || rowValue === undefined) return false;
    const numeric = Number(rowValue);
    if (Number.isNaN(numeric)) return false;
    const comparator = config.comparator || 'gte';
    switch (comparator) {
      case 'gt':
        return numeric > threshold;
      case 'lt':
        return numeric < threshold;
      case 'lte':
        return numeric <= threshold;
      case 'gte':
      default:
        return numeric >= threshold;
    }
  }, []);

  const filtered = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const countrySet = Array.isArray(countryFilter) ? new Set(countryFilter.map((item) => item.toLowerCase())) : null;
    const typeSet = Array.isArray(symbolTypeFilter) ? new Set(symbolTypeFilter.map((item) => item.toLowerCase())) : null;
    const industrySet = Array.isArray(industryFilter) ? new Set(industryFilter.map((item) => item.toLowerCase())) : null;

    return data.filter((row) => {
      for (const { key } of PERIODS) {
        if (!passesGrowthFilter(row[key], filters[key])) {
          return false;
        }
      }

      for (const { key } of FUNDAMENTAL_METRICS) {
        const { mode, value } = fundFilters[key];
        const metricValue = row[key];
        if (mode === 'off') {
          continue;
        }
        if (mode === 'has') {
          if (metricValue === null || metricValue === undefined) return false;
          continue;
        }
        if (mode === 'missing') {
          if (metricValue !== null && metricValue !== undefined) return false;
          continue;
        }
        if (mode === 'gt' || mode === 'lt') {
          const numeric = parseFloat(value);
          if (Number.isNaN(numeric)) {
            continue;
          }
          if (metricValue === null || metricValue === undefined) return false;
          const comparable = Number(metricValue);
          if (mode === 'gt' && !(comparable > numeric)) return false;
          if (mode === 'lt' && !(comparable < numeric)) return false;
        }
      }

      if (countryFilter === null) {
        // include all countries
      } else if (countryFilter.length === 0) {
        return false;
      } else if (countrySet && countrySet.size) {
        const country = (row.country || '').toLowerCase();
        if (!country || !countrySet.has(country)) {
          return false;
        }
      }

      if (symbolTypeFilter === null) {
        // include all
      } else if (symbolTypeFilter.length === 0) {
        return false;
      } else if (typeSet && typeSet.size) {
        const type = (row.symbol_type || '').toLowerCase();
        if (!type || !typeSet.has(type)) {
          return false;
        }
      }

      if (industryFilter === null) {
        // include all
      } else if (industryFilter.length === 0) {
        return false;
      } else if (industrySet && industrySet.size) {
        const industries = Array.isArray(row.industries) ? row.industries : [];
        if (!industries.some((ind) => industrySet.has(String(ind).toLowerCase()))) {
          return false;
        }
      }

      return true;
    });
  }, [countryFilter, data, filters, fundFilters, industryFilter, passesGrowthFilter, symbolTypeFilter]);

  const metadataOptions = useMemo(() => {
    const countries = new Set();
    const types = new Set();
    const industries = new Set();
    if (Array.isArray(data)) {
      data.forEach((row) => {
        if (row.country) {
          countries.add(String(row.country));
        }
        if (row.symbol_type) {
          types.add(String(row.symbol_type));
        }
        if (Array.isArray(row.industries)) {
          row.industries.forEach((ind) => {
            if (ind) industries.add(String(ind));
          });
        }
      });
    }
    const sortFn = (a, b) => a.localeCompare(b);
    return {
      countries: Array.from(countries).sort(sortFn),
      symbolTypes: Array.from(types).sort(sortFn),
      industries: Array.from(industries).sort(sortFn),
    };
  }, [data]);

  useEffect(() => {
    setCountryFilter((prev) => {
      if (prev === null) return prev;
      const valid = prev.filter((item) => metadataOptions.countries.includes(item));
      if (valid.length === prev.length) return prev;
      return valid.length ? valid : null;
    });
  }, [metadataOptions.countries]);

  useEffect(() => {
    setSymbolTypeFilter((prev) => {
      if (prev === null) return prev;
      const valid = prev.filter((item) => metadataOptions.symbolTypes.includes(item));
      if (valid.length === prev.length) return prev;
      return valid.length ? valid : null;
    });
  }, [metadataOptions.symbolTypes]);

  useEffect(() => {
    setIndustryFilter((prev) => {
      if (prev === null) return prev;
      const valid = prev.filter((item) => metadataOptions.industries.includes(item));
      if (valid.length === prev.length) return prev;
      return valid.length ? valid : null;
    });
  }, [metadataOptions.industries]);

  const handleChange = (key) => (evt) => {
    const value = evt.target.value;
    setFilters((prev) => {
      const current = prev[key] ?? { comparator: 'gte', value: '', sell: { enabled: true } };
      if (current.value === value) return prev;
      return {
        ...prev,
        [key]: {
          ...current,
          value,
        },
      };
    });
  };

  const handleComparatorChange = (key) => (evt) => {
    const comparator = evt.target.value;
    setFilters((prev) => {
      const current = prev[key] ?? { comparator: 'gte', value: '', sell: { enabled: true } };
      if (current.comparator === comparator) return prev;
      return {
        ...prev,
        [key]: {
          ...current,
          comparator,
        },
      };
    });
  };

  const handleSellToggle = (key) => (evt) => {
    const enabled = evt.target.checked;
    setFilters((prev) => {
      const current = prev[key] ?? { comparator: 'gte', value: '', sell: { enabled: true } };
      const currentEnabled = current.sell?.enabled !== false;
      if (currentEnabled === enabled) return prev;
      return {
        ...prev,
        [key]: {
          ...current,
          sell: { enabled },
        },
      };
    });
  };

  const handleFundModeChange = (key) => (evt) => {
    const mode = evt.target.value;
    setFundFilters((prev) => ({
      ...prev,
      [key]: {
        mode,
        value: mode === 'gt' || mode === 'lt' ? prev[key].value : '',
      },
    }));
  };

  const handleFundValueChange = (key) => (evt) => {
    const value = evt.target.value;
    setFundFilters((prev) => ({
      ...prev,
      [key]: { ...prev[key], value },
    }));
  };

  const getComparableValue = (row, key) => {
    const value = row[key];
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.toISOString();
    if (key === 'symbol' || key === 'for_date') return String(value);
    return Number(value);
  };

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, direction } = sortState;
    const multiplier = direction === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const aVal = getComparableValue(a, key);
      const bVal = getComparableValue(b, key);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1 * multiplier;
      if (bVal === null) return -1 * multiplier;
      if (typeof aVal === 'string' || typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * multiplier;
      }
      if (aVal === bVal) return 0;
      return (aVal - bVal) * multiplier;
    });
    return arr;
  }, [filtered, sortState]);

  const totalRows = Array.isArray(data) ? data.length : 0;
  const matchCount = sorted.length;
  const liveSymbols = useMemo(() => {
    const seen = new Set();
    const next = [];
    sorted.forEach((row) => {
      const symbol = String(row?.symbol || '').trim().toUpperCase();
      if (!symbol || seen.has(symbol)) return;
      seen.add(symbol);
      next.push(symbol);
    });
    pinnedSymbols.forEach((raw) => {
      const symbol = String(raw || '').trim().toUpperCase();
      if (!symbol || seen.has(symbol)) return;
      seen.add(symbol);
      next.push(symbol);
    });
    if (hoveredSymbol) {
      const symbol = String(hoveredSymbol).trim().toUpperCase();
      if (symbol && !seen.has(symbol)) {
        seen.add(symbol);
        next.push(symbol);
      }
    }
    if (expandedSymbol) {
      const symbol = String(expandedSymbol).trim().toUpperCase();
      if (symbol && !seen.has(symbol)) {
        next.push(symbol);
      }
    }
    return next;
  }, [expandedSymbol, hoveredSymbol, pinnedSymbols, sorted]);
  const { data: livePricePayload } = useLivePricesQuery(liveSymbols, {
    enabled: liveSymbols.length > 0,
  });
  const livePriceMap = livePricePayload?.prices || {};
  const runLiveProbe = useCallback(async () => {
    const symbol = String(probeSymbol || '').trim().toUpperCase();
    if (!symbol) {
      setProbeError('Enter a symbol');
      return;
    }
    setProbeLoading(true);
    setProbeError(null);
    try {
      const payload = await httpJSON(`/v1/prices/live/${encodeURIComponent(symbol)}`);
      setProbeResult(payload);
      setProbeCheckedAt(new Date());
    } catch (err) {
      setProbeResult(null);
      setProbeError(err instanceof Error ? err.message : 'Probe failed');
      setProbeCheckedAt(new Date());
    } finally {
      setProbeLoading(false);
    }
  }, [probeSymbol]);

  const toggleSort = (key) => () => {
    setSortState((prev) => {
      if (prev.key === key) {
        const nextDir = prev.direction === 'asc' ? 'desc' : 'asc';
        return { key, direction: nextDir };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortIndicator = (key) => {
    if (sortState.key !== key) return <span className="sort-indicator">⇅</span>;
    return <span className="sort-indicator">{sortState.direction === 'asc' ? '▲' : '▼'}</span>;
  };

  const handleRowEnter = (symbol) => () => {
    setHoveredSymbol(symbol);
  };

  const handleRowLeave = () => {
    setHoveredSymbol(null);
  };

  const toggleSidebar = useCallback(() => {
    setFiltersCollapsed((prev) => !prev);
  }, []);

  const pinSymbol = useCallback((symbol) => {
    setPinnedSymbols((prev) => (prev.includes(symbol) ? prev : [...prev, symbol]));
  }, []);

  const unpinSymbol = useCallback((symbol) => {
    setPinnedSymbols((prev) => prev.filter((item) => item !== symbol));
    setExpandedSymbol((prev) => (prev === symbol ? null : prev));
  }, []);

  const expandSymbol = useCallback((symbol) => {
    setExpandedSymbol(symbol);
  }, []);

  const maybeFormatBigNumber = (value) => {
    if (value === null || value === undefined) return '—';
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const maybeFormatDecimal = (value, fractionDigits = 2) => {
    if (value === null || value === undefined) return '—';
    return Number(value).toFixed(fractionDigits);
  };

  const periodAverages = useMemo(() => {
    const base = {};
    PERIODS.forEach(({ key }) => {
      base[key] = null;
    });
    if (!sorted.length) {
      return base;
    }
    const next = {};
    PERIODS.forEach(({ key }) => {
      let sum = 0;
      let count = 0;
      sorted.forEach((row) => {
        const value = row[key];
        if (value !== null && value !== undefined) {
          sum += Number(value);
          count += 1;
        }
      });
      next[key] = count ? sum / count : null;
    });
    return next;
  }, [sorted]);

  const toggleMultiSelection = useCallback((option, current, allOptions) => {
    const normalizedOptions = Array.from(allOptions);
    if (current === null) {
      return normalizedOptions.filter((item) => item !== option);
    }
    if (current.includes(option)) {
      const next = current.filter((item) => item !== option);
      return next.length ? next : [];
    }
    const next = [...current, option];
    return next.length === normalizedOptions.length ? null : next;
  }, []);

  const handleCountryToggle = useCallback(
    (option) => {
      setCountryFilter((prev) => toggleMultiSelection(option, prev, metadataOptions.countries));
    },
    [metadataOptions.countries, toggleMultiSelection]
  );

  const handleCountrySetAll = useCallback(() => {
    setCountryFilter(null);
  }, []);

  const handleCountrySetNone = useCallback(() => {
    setCountryFilter([]);
  }, []);

  const handleSymbolTypeToggle = useCallback(
    (option) => {
      setSymbolTypeFilter((prev) => toggleMultiSelection(option, prev, metadataOptions.symbolTypes));
    },
    [metadataOptions.symbolTypes, toggleMultiSelection]
  );

  const handleSymbolTypeSetAll = useCallback(() => {
    setSymbolTypeFilter(null);
  }, []);

  const handleSymbolTypeSetNone = useCallback(() => {
    setSymbolTypeFilter([]);
  }, []);

  const handleIndustryToggle = useCallback(
    (option) => {
      setIndustryFilter((prev) => toggleMultiSelection(option, prev, metadataOptions.industries));
    },
    [metadataOptions.industries, toggleMultiSelection]
  );

  const handleIndustrySetAll = useCallback(() => {
    setIndustryFilter(null);
  }, []);

  const handleIndustrySetNone = useCallback(() => {
    setIndustryFilter([]);
  }, []);

  const formatAverage = useCallback((val) => {
    if (val === null || Number.isNaN(val)) return '—';
    return `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;
  }, []);

  const PinnedSparkWidget = ({ symbol }) => {
    const isExpanded = expandedSymbol === symbol;
    const { data, isLoading, isError } = usePriceSparkQuery(symbol, { staleTime: 5 * 60 * 1000 });
    useEffect(() => {
      if (!data?.series) return;
      const trimmed = filterSeriesToWindow(data.series);
      if (trimmed.length === 0) return;
      setSparkCache((prev) => {
        const prevSeries = prev[symbol];
        if (Array.isArray(prevSeries) && prevSeries.length === trimmed.length) {
          const lastPrev = prevSeries[prevSeries.length - 1];
          const lastCurr = trimmed[trimmed.length - 1];
          if (lastPrev && lastCurr && lastPrev[0] === lastCurr[0] && lastPrev[1] === lastCurr[1]) {
            return prev;
          }
        }
        return { ...prev, [symbol]: trimmed };
      });
    }, [data, symbol]);
    const series = filterSeriesToWindow(sparkCache[symbol] || data?.series || []);
    const hasSeries = series.length >= 2;
    const showSkeleton = !hasSeries && isLoading;
    const showError = !hasSeries && isError;
    const latest = series.length ? Number(series[series.length - 1][1]) : null;
    const first = series.length ? Number(series[0][1]) : null;
    const change = latest !== null && first !== null ? ((latest - first) / Math.abs(first)) * 100 : null;
    const chartHeight = 180;
    const chartData = series.map(([d, v]) => ({ date: d, value: Number(v) }));

    return (
      <div className={`pin-widget${isExpanded ? ' expanded' : ''}`}>
        <header className="pin-header">
          <strong>
            <SymbolWithLivePrice symbol={symbol} price={livePriceMap[symbol]} />
          </strong>
          <div className="pin-actions">
            <button className="ghost sm" type="button" onClick={() => expandSymbol(symbol)}>
              View
            </button>
            <button
              className="ghost sm icon-btn"
              type="button"
              aria-label={`Unpin ${symbol}`}
              onClick={() => unpinSymbol(symbol)}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        </header>
        {showSkeleton ? (
          <div className="sparkline-skeleton" style={{ height: `${chartHeight}px` }} aria-hidden="true" />
        ) : showError ? (
          <p className="subtle">Unable to load.</p>
        ) : !hasSeries ? (
          <p className="subtle">Not enough data.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={chartData} margin={{ left: 0, right: 0, top: 10, bottom: 10 }}>
                <CartesianGrid vertical={false} stroke="#1f2937" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => value.slice(5)}
                  minTickGap={25}
                  stroke="#64748b"
                />
                <YAxis
                  dataKey="value"
                  stroke="#64748b"
                  width={60}
                  tickFormatter={(val) => val.toFixed(2)}
                />
                <ChartTooltip
                  formatter={(val) => [Number(val).toFixed(2), 'Price']}
                  labelFormatter={(label) => label}
                  contentStyle={{ backgroundColor: '#0d1423', borderColor: '#1f2937' }}
                />
                <Line type="monotone" dataKey="value" stroke="#38bdf8" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            <p className="subtle" style={{ marginTop: '.35rem' }}>
              Latest {latest !== null ? latest.toFixed(2) : '—'}
              {change !== null ? ` (${change >= 0 ? '+' : ''}${change.toFixed(2)}%)` : ''}
            </p>
          </>
        )}
      </div>
    );
  };

  const PinnedSparkOverlay = ({ symbol, onClose, cache, setCache }) => {
    const { data, isLoading, isError } = usePriceSparkQuery(symbol, { staleTime: 5 * 60 * 1000 });
    useEffect(() => {
      if (!data?.series) return;
      const trimmed = filterSeriesToWindow(data.series);
      if (trimmed.length === 0) return;
      setCache((prev) => {
        const prevSeries = prev[symbol];
        if (Array.isArray(prevSeries) && prevSeries.length === trimmed.length) {
          const lastPrev = prevSeries[prevSeries.length - 1];
          const lastCurr = trimmed[trimmed.length - 1];
          if (lastPrev && lastCurr && lastPrev[0] === lastCurr[0] && lastPrev[1] === lastCurr[1]) {
            return prev;
          }
        }
        return { ...prev, [symbol]: trimmed };
      });
    }, [data, symbol, setCache]);
    useEffect(() => {
      const handler = (event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }, [onClose]);
    const series = filterSeriesToWindow(cache[symbol] || data?.series || []);
    const hasSeries = series.length >= 2;
    const showSkeleton = !hasSeries && isLoading;
    const showError = !hasSeries && isError;
    const chartData = series.map(([d, v]) => ({ date: d, value: Number(v) }));

    const overlay = (
      <div className="pin-overlay" role="dialog" aria-modal="true" onClick={onClose}>
        <div className="pin-overlay-content" onClick={(e) => e.stopPropagation()}>
          <header className="pin-header">
            <strong>
              <SymbolWithLivePrice symbol={symbol} price={livePriceMap[symbol]} /> — full history
            </strong>
            <div className="pin-actions">
              <button className="ghost sm" type="button" onClick={onClose}>
                Close
              </button>
            </div>
          </header>
          {showSkeleton ? (
            <div className="pin-overlay-chart">
              <div className="sparkline-skeleton" style={{ height: '100%' }} aria-hidden="true" />
            </div>
          ) : showError ? (
            <p className="subtle">Unable to load.</p>
          ) : !hasSeries ? (
            <p className="subtle">Not enough data.</p>
          ) : (
            <>
              <div className="pin-overlay-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ left: 0, right: 20, top: 20, bottom: 20 }}>
                    <CartesianGrid vertical={false} stroke="#1f2937" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => value}
                      minTickGap={30}
                      stroke="#64748b"
                    />
                    <YAxis
                      dataKey="value"
                      stroke="#64748b"
                      width={90}
                      tickFormatter={(val) => val.toFixed(2)}
                    />
                    <ChartTooltip
                      formatter={(val) => [Number(val).toFixed(2), 'Price']}
                      labelFormatter={(label) => label}
                      contentStyle={{ backgroundColor: '#0d1423', borderColor: '#1f2937' }}
                    />
                    <Line type="monotone" dataKey="value" stroke="#38bdf8" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="subtle" style={{ marginTop: '.5rem' }}>
                {`Span: ${series[0][0]} → ${series[series.length - 1][0]}`}
              </p>
              <p className="subtle">
                Latest {series.length ? Number(series[series.length - 1][1]).toFixed(2) : '—'}
              </p>
            </>
          )}
        </div>
      </div>
    );

    return ReactDOM.createPortal(overlay, document.body);
  };

  return (
    <section className="card growth-card">
      <header className="card-header">
        <h2 className="card-title">Growth & Fundamentals Filters</h2>
        <div className="header-actions">
          <div className="watchlist-name-block">
            <span className="watchlist-name-label">Watchlist:</span>
            {isEditingName ? (
              <input
                ref={nameInputRef}
                className="watchlist-name-input"
                value={nameDraft}
                onChange={(evt) => setNameDraft(evt.target.value)}
                onBlur={commitNameEdit}
                onKeyDown={(evt) => {
                  if (evt.key === 'Enter') {
                    evt.preventDefault();
                    commitNameEdit();
                  } else if (evt.key === 'Escape') {
                    evt.preventDefault();
                    cancelNameEdit();
                  }
                }}
              />
            ) : (
              <button
                type="button"
                className="watchlist-name-display"
                onClick={() => setIsEditingName(true)}
                aria-label={hasActiveWatchlist ? 'Rename watchlist' : 'Set watchlist name'}
              >
                <span className="watchlist-name-value">{nameDraft}</span>
                <span className="watchlist-name-pencil" aria-hidden="true">✎</span>
              </button>
            )}
            <button className="ghost xs" type="button" onClick={() => {
              setActiveWatchlist(null);
              setNameDraft(DEFAULT_WATCHLIST_NAME);
              setIsEditingName(false);
              setAutoSaveEnabled(false);
              syncUrl(null);
            }}>
              New
            </button>
            {!hasActiveWatchlist ? (
              <span className="watchlist-name-unsaved">(unsaved)</span>
            ) : null}
          </div>
          <button
            className="ghost sm"
            type="button"
            onClick={handleSaveWatchlist}
            disabled={hasActiveWatchlist ? updatingWatchlist : creatingWatchlist}
          >
            {(hasActiveWatchlist ? updatingWatchlist : creatingWatchlist) ? 'Saving…' : 'Save'}
          </button>
          <button className="ghost sm" type="button" onClick={handleOpenLoadModal}>
            Manage Watchlists
          </button>
          <label className={`auto-save-toggle${autoSaveEnabled ? ' checked' : ''}${!hasActiveWatchlist ? ' disabled' : ''}`}>
            <input
              type="checkbox"
              checked={autoSaveEnabled}
              onChange={handleAutoSaveToggle}
              disabled={!hasActiveWatchlist}
            />
            <span>Auto save</span>
          </label>
          <button className="ghost sm" type="button" onClick={toggleSidebar}>
            {filtersCollapsed ? 'Show Filters' : 'Hide Filters'}
          </button>
          <button className="ghost sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      <div className="watchlist-status">
        {hasActiveWatchlist && hasChangesFromActive ? (
          <span className="watchlist-status__dirty">Unsaved changes</span>
        ) : null}
        {watchlistError ? <span className="error-text">{watchlistError}</span> : null}
      </div>

      <div className="live-probe">
        <div className="live-probe__title">Live Price Probe (Temporary)</div>
        <div className="live-probe__controls">
          <input
            className="text"
            value={probeSymbol}
            onChange={(evt) => setProbeSymbol(evt.target.value.toUpperCase())}
            placeholder="AAPL"
            maxLength={24}
          />
          <button className="ghost sm" type="button" onClick={runLiveProbe} disabled={probeLoading}>
            {probeLoading ? 'Checking…' : 'Test Live'}
          </button>
          {probeResult?.price != null ? (
            <span className="live-probe__ok">PASS</span>
          ) : probeCheckedAt ? (
            <span className="live-probe__fail">NO LIVE PRICE</span>
          ) : null}
        </div>
        {probeResult ? (
          <div className="live-probe__result subtle">
            {probeResult.symbol}: {probeResult.price != null ? Number(probeResult.price).toFixed(2) : 'unavailable'}
          </div>
        ) : null}
        {probeError ? <div className="live-probe__error">{probeError}</div> : null}
      </div>

      <div className="growth-preview">
        <SparklinePreview
          symbol={hoveredSymbol}
          cache={sparkCache}
          setCache={setSparkCache}
          livePrice={hoveredSymbol ? livePriceMap[hoveredSymbol] : null}
        />
      </div>

      <div className="growth-layout">
        <div className="growth-main">
          <div className={`growth-main-grid${filtersCollapsed ? ' collapsed' : ''}`}>
            {!filtersCollapsed ? (
              <aside className="filters-column">
                <div className="filters-panel">
                <div className="filters-section">
                  {PERIODS.map(({ key, label }) => {
                    const current = filters[key] || { comparator: 'gte', value: '', sell: { enabled: true } };
                    const sellEnabled = current.sell?.enabled !== false;
                    return (
                      <div key={key} className="filter-field growth-filter">
                        <span>{label}</span>
                        <div className="growth-filter__controls">
                          <span className="filter-input-group">
                            <select value={current.comparator} onChange={handleComparatorChange(key)}>
                              {GROWTH_COMPARATORS.map((mode) => (
                                <option key={mode.value} value={mode.value}>
                                  {mode.label}
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.1"
                              value={current.value}
                              onChange={handleChange(key)}
                              placeholder="0"
                            />
                          </span>
                          <label className="sell-toggle">
                            <input type="checkbox" checked={sellEnabled} onChange={handleSellToggle(key)} />
                            <span>Sell</span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="filters-section">
                  {FUNDAMENTAL_METRICS.map(({ key, label, step }) => {
                    const current = fundFilters[key];
                    const needsInput = current.mode === 'gt' || current.mode === 'lt';
                    return (
                      <label key={key} className="filter-field fund-filter">
                        <span>{label}</span>
                        <span className="filter-input-group">
                          <select value={current.mode} onChange={handleFundModeChange(key)}>
                            {FUND_FILTER_MODES.map((mode) => (
                              <option key={mode.value} value={mode.value}>
                                {mode.label}
                              </option>
                            ))}
                          </select>
                          {needsInput ? (
                            <input
                              type="number"
                              inputMode="decimal"
                              step={step ?? '0.01'}
                              value={current.value}
                              onChange={handleFundValueChange(key)}
                              placeholder="0"
                            />
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="metadata-filters">
                  <MultiSelectFilter
                    label="Country"
                    options={metadataOptions.countries}
                    selected={countryFilter}
                    search={countrySearch}
                    onSearchChange={setCountrySearch}
                    onToggle={handleCountryToggle}
                    onSetAll={handleCountrySetAll}
                    onSetNone={handleCountrySetNone}
                  />
                  <MultiSelectFilter
                    label="Type"
                    options={metadataOptions.symbolTypes}
                    selected={symbolTypeFilter}
                    search={symbolTypeSearch}
                    onSearchChange={setSymbolTypeSearch}
                    onToggle={handleSymbolTypeToggle}
                    onSetAll={handleSymbolTypeSetAll}
                    onSetNone={handleSymbolTypeSetNone}
                  />
                  <MultiSelectFilter
                    label="Industries"
                    options={metadataOptions.industries}
                    selected={industryFilter}
                    search={industrySearch}
                    onSearchChange={setIndustrySearch}
                    onToggle={handleIndustryToggle}
                    onSetAll={handleIndustrySetAll}
                    onSetNone={handleIndustrySetNone}
                  />
                </div>
              </div>
              </aside>
            ) : null}

            <div className="table-panel">
              <p className="filter-summary subtle">
                Showing {matchCount.toLocaleString()} of {totalRows.toLocaleString()} symbols
              </p>

              {isLoading ? (
                <p className="subtle">Loading growth data…</p>
              ) : isError ? (
                <div className="error-box">
                  <p>Unable to load growth results.</p>
                  <p className="subtle" style={{ marginTop: '.35rem' }}>{String(error?.message || error)}</p>
                </div>
              ) : (
                <>
                  <div className="table-wrap">
                    <table className="growth-table">
                      <thead>
                        <tr>
                          <th className="sortable" onClick={toggleSort('symbol')}>
                            <div className="col-header">
                              <span>Symbol {renderSortIndicator('symbol')}</span>
                            </div>
                          </th>
                          <th className="sortable" onClick={toggleSort('company')}>
                            <div className="col-header">
                              <span>Company {renderSortIndicator('company')}</span>
                            </div>
                          </th>
                          <th className="sortable" onClick={toggleSort('latest_price')}>
                            <div className="col-header">
                              <span>Price {renderSortIndicator('latest_price')}</span>
                            </div>
                          </th>
                          {PERIODS.map(({ key, label }) => (
                            <th key={key} className="sortable" onClick={toggleSort(key)}>
                              <div className="col-header">
                                <span>{label} {renderSortIndicator(key)}</span>
                                <span className="col-average">{formatAverage(periodAverages[key])}</span>
                              </div>
                            </th>
                          ))}
                          {FUNDAMENTAL_METRICS.map(({ key, label }) => (
                            <th key={key} className="sortable" onClick={toggleSort(key)}>
                              <div className="col-header">
                                <span>{label} {renderSortIndicator(key)}</span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.length === 0 ? (
                          <tr>
                            <td colSpan={PERIODS.length + FUNDAMENTAL_METRICS.length + 2} className="empty">
                              No symbols meet the current thresholds.
                            </td>
                          </tr>
                        ) : (
                          sorted.map((row) => (
                            <tr
                              key={`${row.symbol}-${row.for_date}`}
                              onMouseEnter={handleRowEnter(row.symbol)}
                              onMouseLeave={handleRowLeave}
                            >
                              <td className="symbol-cell">
                                <SymbolWithLivePrice symbol={row.symbol} price={livePriceMap[row.symbol]} />
                                <button
                                  className="pin-inline ghost sm icon-btn"
                                  type="button"
                                  aria-label={`${pinnedSymbols.includes(row.symbol) ? 'Unpin' : 'Pin'} ${row.symbol}`}
                                  onClick={(evt) => {
                                    evt.stopPropagation();
                                    pinnedSymbols.includes(row.symbol)
                                      ? unpinSymbol(row.symbol)
                                      : pinSymbol(row.symbol);
                                  }}
                                >
                                  <span aria-hidden="true">
                                    {pinnedSymbols.includes(row.symbol) ? '✕' : '📌'}
                                  </span>
                                </button>
                              </td>
                              <td className="company-cell">{row.company || '—'}</td>
                              <td>
                                {row.latest_price !== null && row.latest_price !== undefined
                                  ? row.latest_price.toFixed(2)
                                  : '—'}
                              </td>
                              {PERIODS.map(({ key }) => (
                                <td key={key}>
                                  {row[key] === null || row[key] === undefined
                                    ? '—'
                                    : `${Number(row[key]).toFixed(2)}%`}
                                </td>
                              ))}
                              {FUNDAMENTAL_METRICS.map(({ key }) => (
                                <td key={key}>
                                  {key === 'ev_to_ebitda'
                                    ? maybeFormatDecimal(row[key])
                                    : maybeFormatBigNumber(row[key])}
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <section className="pinned-section">
                    <header className="pinned-header">
                      <h3>Pinned Symbols</h3>
                      {pinnedSymbols.length ? (
                        <span className="subtle">{pinnedSymbols.length}</span>
                      ) : null}
                    </header>
                    {pinnedSymbols.length > 0 ? (
                      <div className="pinned-grid">
                        {pinnedSymbols.map((symbol) => (
                          <PinnedSparkWidget key={symbol} symbol={symbol} />
                        ))}
                      </div>
                    ) : (
                      <div className="pinboard-placeholder subtle">Pin symbols to keep them handy.</div>
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isWatchlistOpen
        ? ReactDOM.createPortal(
          <div className="watchlist-overlay" role="dialog" aria-modal="true">
            <div className="watchlist-backdrop" onClick={() => setIsWatchlistOpen(false)} />
            <div className="watchlist-panel" onClick={(event) => event.stopPropagation()}>
              <header className="watchlist-header">
                <div>
                  <h3>Watchlists</h3>
                  <p className="subtle">
                    {activeWatchlist ? `Active: ${activeWatchlist.name}` : 'Load and manage saved filter sets'}
                  </p>
                </div>
                <button className="ghost sm" type="button" onClick={() => setIsWatchlistOpen(false)}>
                  Close
                </button>
              </header>

              {watchlistsFetchError ? (
                <div className="error-box" style={{ marginBottom: '0.75rem' }}>
                  <p>Unable to load watchlists.</p>
                  <p className="subtle" style={{ marginTop: '.25rem' }}>{watchlistsFetchError}</p>
                </div>
              ) : null}

              {watchlistError ? (
                <div className="error-box" style={{ marginBottom: '0.75rem' }}>
                  <p>{watchlistError}</p>
                </div>
              ) : null}

              <section className="watchlist-section">
                <div className="watchlist-list-header">
                  <h4>Existing watchlists</h4>
                  <button
                    className="ghost xs"
                    type="button"
                    onClick={() => refetchWatchlists()}
                    disabled={watchlistsLoading}
                  >
                    {watchlistsLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>

                {watchlistsLoading ? (
                  <p className="subtle">Loading watchlists…</p>
                ) : watchlists.length === 0 ? (
                  <p className="subtle">No watchlists yet.</p>
                ) : (
                  <ul className="watchlist-items">
                    {watchlists.map((watchlist) => {
                      const isActive = activeWatchlist?.id === watchlist.id;
                      const isDeleting = pendingDeleteId === watchlist.id;
                      const handleClick = () => handleLoadWatchlist(watchlist);
                      return (
                        <li
                          key={watchlist.id}
                          className={`watchlist-item${isActive ? ' active' : ''}`}
                          onClick={handleClick}
                        >
                          <button
                            type="button"
                            className="watchlist-item-meta"
                            onClick={handleClick}
                          >
                            <strong>{watchlist.name}</strong>
                            <span className="subtle">Updated {formatWatchlistTime(watchlist.updated_at)}</span>
                          </button>
                          <div className="watchlist-item-actions">
                            <button
                              className="ghost xs danger"
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteWatchlist(watchlist);
                              }}
                              disabled={isDeleting}
                            >
                              {isDeleting ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </div>,
          document.body,
        )
        : null}

      {isNameModalOpen
        ? ReactDOM.createPortal(
          <div className="name-modal-overlay" role="dialog" aria-modal="true">
            <div className="name-modal-backdrop" onClick={handleNameModalCancel} />
            <div className="name-modal" onClick={(event) => event.stopPropagation()}>
              <h3>Name this watchlist</h3>
              <p className="subtle">Choose a unique name so you can load it later.</p>
              <input
                ref={nameModalInputRef}
                className="name-modal-input"
                value={nameModalValue}
                onChange={(evt) => setNameModalValue(evt.target.value)}
                placeholder="e.g. Momentum Screen"
              />
              {nameModalError ? <span className="error-text">{nameModalError}</span> : null}
              <div className="name-modal-actions">
                <button className="ghost sm" type="button" onClick={handleNameModalCancel}>
                  Cancel
                </button>
                <button className="ghost sm" type="button" onClick={handleNameModalConfirm} disabled={creatingWatchlist}>
                  {creatingWatchlist ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}

      {expandedSymbol ? (
        <PinnedSparkOverlay
          symbol={expandedSymbol}
          onClose={() => setExpandedSymbol(null)}
          cache={sparkCache}
          setCache={setSparkCache}
        />
      ) : null}
      {watchlistMessage ? (
        <div className="toast toast-success" role="status">
          {watchlistMessage}
        </div>
      ) : null}
    </section>
  );
}
